import React, { useState } from 'react';
import { Image, ImageProps, View, Text, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, fontSizes, spacing } from '../styles/theme';
import { api } from '../api/remembrApi';

interface SmartImageProps extends Omit<ImageProps, 'source' | 'onError'> {
  // Primary props
  fileId?: number | string;
  initialUrl?: string;
  // Backwards-compat alias for initialUrl
  uri?: string | null;
  // Backwards-compat alias for fallbackIcon
  placeholder?: string;
  fallbackIcon?: string;
  onRefreshNeeded?: () => void;
  onLoadSuccess?: () => void;
  onSmartError?: (error: string) => void;
  showRetryButton?: boolean;
  maxRetries?: number;
}

export const SmartImage: React.FC<SmartImageProps> = ({
  fileId,
  initialUrl,
  uri,
  placeholder,
  fallbackIcon = 'image',
  style,
  onLoadSuccess,
  onSmartError,
  showRetryButton = true,
  ...imageProps
}) => {
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [currentUrl, setCurrentUrl] = useState(initialUrl ?? uri ?? undefined);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Function to refresh URL using file ID
  const refreshImageUrl = async () => {
    if (!fileId || isRefreshing) return false;
    
    try {
      setIsRefreshing(true);
      console.log('🔄 Attempting to refresh URL for file ID:', fileId);
      
      const fileDetails = await api.refreshFileUrls([fileId as number]);
      const newUrl = fileDetails?.refreshed_urls?.[0]?.signed_url;
      
      if (newUrl && newUrl !== currentUrl) {
        console.log('✅ Got fresh URL:', newUrl);
        setCurrentUrl(newUrl);
        setHasError(false);
        return true;
      } else {
        console.log('⚠️ No new URL available or same URL returned');
        return false;
      }
    } catch (error) {
      console.error('❌ Failed to refresh URL:', error);
      return false;
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!currentUrl) {
    const effectiveIcon = (placeholder || fallbackIcon) as any;
    return (
      <View style={[style, styles.placeholderContainer]}>
        <Ionicons 
          name={effectiveIcon} 
          size={24} 
          color={colors.gray400} 
        />
        <Text style={styles.placeholderText}>No image available</Text>
      </View>
    );
  }

  if (hasError) {
    return (
      <View style={[style, styles.errorContainer]}>
        <Ionicons 
          name="image-outline" 
          size={24} 
          color={colors.gray400} 
        />
        <Text style={styles.errorText}>Image unavailable</Text>
        <Text style={styles.urlText} numberOfLines={1}>
          {currentUrl?.includes('expired') ? 'URL expired' : 'Failed to load'}
        </Text>
        {showRetryButton && retryCount < 2 && (
          <TouchableOpacity
            style={styles.retryButton}
            onPress={async () => {
              console.log('🔄 Retrying image load...');
              setRetryCount(prev => prev + 1);
              
              // First try to refresh URL if we have a file ID
              if (fileId && retryCount === 0) {
                const refreshSuccess = await refreshImageUrl();
                if (refreshSuccess) {
                  console.log('✅ URL refreshed, will retry with new URL');
                  return; // New URL will trigger re-render
                }
              }
              
              // Fallback to simple retry with current URL
              console.log('🔄 Simple retry with current URL:', currentUrl);
              setHasError(false);
            }}
            disabled={isRefreshing}
          >
            <Text style={styles.retryText}>
              {isRefreshing ? 'Refreshing...' : 'Retry'}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  return (
    <Image
      {...imageProps}
      source={{ uri: currentUrl || '' }}
      style={style}
      onLoad={() => {
        onLoadSuccess?.();
      }}
      onError={async (error) => {
        console.log('❌ Image load failed:', currentUrl, error.nativeEvent.error);
        // Auto-retry with URL refresh on first failure
        if (fileId && retryCount === 0) {
          console.log('🔄 Auto-refreshing URL on first failure...');
          const refreshSuccess = await refreshImageUrl();
          if (refreshSuccess) {
            console.log('✅ URL auto-refreshed, will retry automatically');
            return; // Don't set error state, let it retry with new URL
          }
        }
        
        setHasError(true);
        onSmartError?.(`Image failed to load: ${error.nativeEvent.error || 'Unknown error'}`);
      }}
    />
  );
};

const styles = {
  placeholderContainer: {
    backgroundColor: colors.gray100,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    padding: spacing.sm,
  },
  placeholderText: {
    fontSize: fontSizes.xs,
    color: colors.gray500,
    marginTop: spacing.xs,
    textAlign: 'center' as const,
  },
  errorContainer: {
    backgroundColor: colors.gray100,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    padding: spacing.sm,
  },
  errorText: {
    fontSize: fontSizes.xs,
    color: colors.gray500,
    marginTop: spacing.xs,
    textAlign: 'center' as const,
  },
  urlText: {
    fontSize: fontSizes.xs,
    color: colors.gray400,
    marginTop: 2,
    textAlign: 'center' as const,
  },
  retryButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 4,
    marginTop: spacing.xs,
  },
  retryText: {
    fontSize: fontSizes.xs,
    color: colors.background,
    fontWeight: '600' as const,
  },
}; 
